// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { isArray } from "@utils/arrayUtils";
// @ts-ignore
import { parseJsonWithNumbersAsBigint, renderJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
// @ts-ignore
import { toBase64 } from "@utils/blobUtils";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
import type { ChannelTextLocalization, Tag, Language, ChannelMetadata } from "../schema";
import { serialize_ChannelTextLocalization_internal, deserialize_ChannelTextLocalization_internal } from "./ChannelTextLocalization";
import { serialize_Tag_internal, deserialize_Tag_internal } from "./Tag";
import { serialize_Language_internal, deserialize_Language_internal } from "./Language";

export function serialize_ChannelMetadata_internal(value: ChannelMetadata): unknown {
  return ((value: {
    activationTime: DateTime<true> | null;
    active: boolean;
    channelId: bigint;
    channelLocalization: { additionalProperties: Record<string, ChannelTextLocalization> };
    coverUri: string | null;
    definition: string | null;
    descriptionHtml: string | null;
    modificationTime: DateTime<true> | null;
    name: string;
    synonyms: string | null;
    tag: Tag;
    title: string | null;
    visibleLanguages: Language[];
  }): unknown => {
    return {
      activationTime: ((value: DateTime<true> | null): unknown => {
        if (value === null) {
          return null;
        }
        return value.toMillis();
      })(value["activationTime"]),
      active: ((value: boolean): unknown => {
        return value;
      })(value["active"]),
      channelId: ((value: bigint): unknown => {
        return value;
      })(value["channelId"]),
      channelLocalization: ((value: { additionalProperties: Record<string, ChannelTextLocalization> }): unknown => {
        const unordered_obj: Record<string, unknown> = {
          ...Object.fromEntries(
            Object.entries(value.additionalProperties).map(([propertyKey, propertyValue]) => {
              return [
                propertyKey,
                ((value: ChannelTextLocalization): unknown => {
                  return serialize_ChannelTextLocalization_internal(value);
                })(propertyValue),
              ];
            }),
          ),
        };
        return Object.keys(unordered_obj)
          .sort()
          .reduce(
            (obj, key) => {
              obj[key] = unordered_obj[key];
              return obj;
            },
            {} as Record<string, unknown>,
          );
      })(value["channelLocalization"]),
      coverUri: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["coverUri"]),
      definition: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["definition"]),
      descriptionHtml: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["descriptionHtml"]),
      modificationTime: ((value: DateTime<true> | null): unknown => {
        if (value === null) {
          return null;
        }
        return value.toMillis();
      })(value["modificationTime"]),
      name: ((value: string): unknown => {
        return value;
      })(value["name"]),
      synonyms: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["synonyms"]),
      tag: ((value: Tag): unknown => {
        return serialize_Tag_internal(value);
      })(value["tag"]),
      title: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["title"]),
      visibleLanguages: ((value: Language[]): unknown => {
        return value.map((value: Language): unknown => {
          return serialize_Language_internal(value);
        });
      })(value["visibleLanguages"]),
    };
  })(value);
}
export function serialize_ChannelMetadata(obj: ChannelMetadata): string {
  return renderJsonWithNumbersAsBigint(serialize_ChannelMetadata_internal(obj));
}
export function deserialize_ChannelMetadata_internal(value: unknown): ParserResult<ChannelMetadata> {
  return ((
    value: unknown,
  ): ParserResult<{
    activationTime: DateTime<true> | null;
    active: boolean;
    channelId: bigint;
    channelLocalization: { additionalProperties: Record<string, ChannelTextLocalization> };
    coverUri: string | null;
    definition: string | null;
    descriptionHtml: string | null;
    modificationTime: DateTime<true> | null;
    name: string;
    synonyms: string | null;
    tag: Tag;
    title: string | null;
    visibleLanguages: Language[];
  }> => {
    if (typeof value !== "object") {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata]: '${value}' expected to be of type 'object'.` };
    }
    if (value === null) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata]: '${value}' expected to not be null.` };
    }
    if (!hasProp(value, "activationTime")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/activationTime]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'activationTime'.` };
    }
    const parsed_activationTime = ((): ParserResult<DateTime<true> | null> => {
      return ((value: unknown): ParserResult<DateTime<true> | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "number") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/activationTime]: '${value}' expected to be of type number.` };
        }
        const parsed = DateTime.fromMillis(value);
        if (!parsed.isValid) {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/activationTime]: '${value}' expected to be valid DateTime.` };
        }
        return { success: true, value: parsed };
      })(value["activationTime"]);
    })();
    if (!parsed_activationTime.success) {
      return { success: false, reason: parsed_activationTime.reason };
    }
    if (!hasProp(value, "active")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/active]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'active'.` };
    }
    const parsed_active = ((): ParserResult<boolean> => {
      return ((value: unknown): ParserResult<boolean> => {
        if (typeof value !== "boolean") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/active]: '${value}' expected to be of type boolean.` };
        }
        return { success: true, value: value };
      })(value["active"]);
    })();
    if (!parsed_active.success) {
      return { success: false, reason: parsed_active.reason };
    }
    if (!hasProp(value, "channelId")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/channelId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'channelId'.` };
    }
    const parsed_channelId = ((): ParserResult<bigint> => {
      return ((value: unknown): ParserResult<bigint> => {
        if (typeof value === "number") {
          return { success: true, value: BigInt(value) };
        }
        if (typeof value === "bigint") {
          return { success: true, value: value };
        }
        return { success: false, reason: `[#/components/schemas/ChannelMetadata/channelId]: '${value}' expected to be of type bigint or number.` };
      })(value["channelId"]);
    })();
    if (!parsed_channelId.success) {
      return { success: false, reason: parsed_channelId.reason };
    }
    if (!hasProp(value, "channelLocalization")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/channelLocalization]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'channelLocalization'.` };
    }
    const parsed_channelLocalization = ((): ParserResult<{ additionalProperties: Record<string, ChannelTextLocalization> }> => {
      return ((value: unknown): ParserResult<{ additionalProperties: Record<string, ChannelTextLocalization> }> => {
        if (typeof value !== "object") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/channelLocalization]: '${value}' expected to be of type 'object'.` };
        }
        if (value === null) {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/channelLocalization]: '${value}' expected to not be null.` };
        }
        const parsed_additionalProperties = ((): ParserResult<Record<string, ChannelTextLocalization>> => {
          const parsed = Object.entries(value).map(([key, value]): [string, ParserResult<ChannelTextLocalization>] => {
            return [
              key,
              ((value: unknown): ParserResult<ChannelTextLocalization> => {
                return deserialize_ChannelTextLocalization_internal(value);
              })(value),
            ];
          });
          if (parsed.find(([_, value]) => !value.success) !== undefined) {
            return {
              success: false,
              reason: `[#/components/schemas/ChannelMetadata/channelLocalization]: Reason(s): '${parsed
                .map(([_, value]) => value)
                .filter((p) => !p.success)
                .map((p) => p.reason)
                .join(", ")}'.`,
            };
          }
          return {
            success: true,
            value: Object.fromEntries(
              parsed.map(([key, value]) => {
                assert(value.success);
                return [key, value.value];
              }),
            ),
          };
        })();
        if (!parsed_additionalProperties.success) {
          return { success: false, reason: parsed_additionalProperties.reason };
        }
        return { success: true, value: { additionalProperties: parsed_additionalProperties.value } };
      })(value["channelLocalization"]);
    })();
    if (!parsed_channelLocalization.success) {
      return { success: false, reason: parsed_channelLocalization.reason };
    }
    if (!hasProp(value, "coverUri")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/coverUri]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'coverUri'.` };
    }
    const parsed_coverUri = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/coverUri]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["coverUri"]);
    })();
    if (!parsed_coverUri.success) {
      return { success: false, reason: parsed_coverUri.reason };
    }
    if (!hasProp(value, "definition")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/definition]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'definition'.` };
    }
    const parsed_definition = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/definition]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["definition"]);
    })();
    if (!parsed_definition.success) {
      return { success: false, reason: parsed_definition.reason };
    }
    if (!hasProp(value, "descriptionHtml")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/descriptionHtml]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'descriptionHtml'.` };
    }
    const parsed_descriptionHtml = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/descriptionHtml]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["descriptionHtml"]);
    })();
    if (!parsed_descriptionHtml.success) {
      return { success: false, reason: parsed_descriptionHtml.reason };
    }
    if (!hasProp(value, "modificationTime")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/modificationTime]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'modificationTime'.` };
    }
    const parsed_modificationTime = ((): ParserResult<DateTime<true> | null> => {
      return ((value: unknown): ParserResult<DateTime<true> | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "number") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/modificationTime]: '${value}' expected to be of type number.` };
        }
        const parsed = DateTime.fromMillis(value);
        if (!parsed.isValid) {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/modificationTime]: '${value}' expected to be valid DateTime.` };
        }
        return { success: true, value: parsed };
      })(value["modificationTime"]);
    })();
    if (!parsed_modificationTime.success) {
      return { success: false, reason: parsed_modificationTime.reason };
    }
    if (!hasProp(value, "name")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/name]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'name'.` };
    }
    const parsed_name = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/name]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["name"]);
    })();
    if (!parsed_name.success) {
      return { success: false, reason: parsed_name.reason };
    }
    if (!hasProp(value, "synonyms")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/synonyms]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'synonyms'.` };
    }
    const parsed_synonyms = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/synonyms]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["synonyms"]);
    })();
    if (!parsed_synonyms.success) {
      return { success: false, reason: parsed_synonyms.reason };
    }
    if (!hasProp(value, "tag")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/tag]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'tag'.` };
    }
    const parsed_tag = ((): ParserResult<Tag> => {
      return ((value: unknown): ParserResult<Tag> => {
        return deserialize_Tag_internal(value);
      })(value["tag"]);
    })();
    if (!parsed_tag.success) {
      return { success: false, reason: parsed_tag.reason };
    }
    if (!hasProp(value, "title")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/title]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'title'.` };
    }
    const parsed_title = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/title]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["title"]);
    })();
    if (!parsed_title.success) {
      return { success: false, reason: parsed_title.reason };
    }
    if (!hasProp(value, "visibleLanguages")) {
      return { success: false, reason: `[#/components/schemas/ChannelMetadata/visibleLanguages]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'visibleLanguages'.` };
    }
    const parsed_visibleLanguages = ((): ParserResult<Language[]> => {
      return ((value: unknown): ParserResult<Language[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/ChannelMetadata/visibleLanguages]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<Language> => {
          return deserialize_Language_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/ChannelMetadata/visibleLanguages]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["visibleLanguages"]);
    })();
    if (!parsed_visibleLanguages.success) {
      return { success: false, reason: parsed_visibleLanguages.reason };
    }
    return {
      success: true,
      value: {
        activationTime: parsed_activationTime.value,
        active: parsed_active.value,
        channelId: parsed_channelId.value,
        channelLocalization: parsed_channelLocalization.value,
        coverUri: parsed_coverUri.value,
        definition: parsed_definition.value,
        descriptionHtml: parsed_descriptionHtml.value,
        modificationTime: parsed_modificationTime.value,
        name: parsed_name.value,
        synonyms: parsed_synonyms.value,
        tag: parsed_tag.value,
        title: parsed_title.value,
        visibleLanguages: parsed_visibleLanguages.value,
      },
    };
  })(value);
}
export function deserialize_ChannelMetadata(str: string): ParserResult<ChannelMetadata> {
  return deserialize_ChannelMetadata_internal(parseJsonWithNumbersAsBigint(str));
}
